import { array } from 'fp-ts';
import { PortfolioEnterpriseGroupResult, StringFilter } from '@mews-commander/data-access';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import {
    useTableState,
    TableProvider,
    TableHeader,
    TableActiveColumnFilters,
    Table,
    usePaginatedData,
    ActionToolbar,
    useFullRowSelection,
    ActionToolbarButton,
    IconName,
} from '@optimus-web/core';
import { useGeneralEnterprisesByIds } from '../../queries';
import { GeneralEnterpriseWithCityName } from '../types';
import { getFilteredProperties } from '../utils';
import { DeletePropertiesWrapper } from '../DeletePropertiesWrapper';
import { useColumns } from './tableMapping';

type UsedKeys = keyof Pick<GeneralEnterpriseWithCityName, 'name' | 'cityName'>;
type GeneralEnterpriseWithCityNameFilters = Partial<Record<UsedKeys, Array<StringFilter> | null>>;

export const PropertyGroupsPropertiesTable = ({ propertyGroup }: { propertyGroup: PortfolioEnterpriseGroupResult }) => {
    const [tableState, setTableState] = useTableState<
        GeneralEnterpriseWithCityName,
        GeneralEnterpriseWithCityNameFilters
    >();
    const { data, isFetching } = useGeneralEnterprisesByIds(propertyGroup?.propertyIds || null);
    const enterprises = (array.isNonEmpty(propertyGroup?.propertyIds) && data?.enterprises) || [];

    const cities = data?.cities || [];

    const enterprisesWithCities: GeneralEnterpriseWithCityName[] = enterprises.map(({ cityId, ...rest }) => ({
        ...rest,
        cityId,
        cityName: cities?.find((city) => city.id === cityId)?.name ?? '',
    }));

    const { data: properties, totalRowCount } = usePaginatedData(
        getFilteredProperties({
            data: enterprisesWithCities,
            nameSearchTerm: tableState.filters.name?.[0].value,
            cityNameSearchTerm: tableState.filters.cityName?.[0].value,
            nameSortOrder: tableState.sort.name?.order,
            cityNameSortOrder: tableState.sort?.cityName?.order,
        }),
        tableState.pagination
    );

    const { rowSelection, selectedRowsData, setRowSelection, selectedRowsIds, hasRowsSelected, resetRowSelection } =
        useFullRowSelection(data?.enterprises);

    const columns = useColumns({ propertyGroup, onDeleteSuccess: resetRowSelection });

    return (
        <TableProvider columns={columns} state={tableState} onStateChange={setTableState}>
            <TableHeader
                title={<LocalizedText textKey={TextKey.PropertiesNavigation} />}
                subTitle={totalRowCount || '0'}
            />

            <ActionToolbar
                disabled={!hasRowsSelected}
                leftSide={
                    array.isNonEmpty(selectedRowsIds) && (
                        <LocalizedText
                            textKey={TextKey.SelectedItems}
                            substitutes={{ count: selectedRowsIds.length }}
                        />
                    )
                }
                rightSide={
                    <DeletePropertiesWrapper
                        propertyGroup={propertyGroup}
                        selectedProperties={selectedRowsData}
                        onSuccess={resetRowSelection}
                        renderButtonComponent={(onClick) => (
                            <ActionToolbarButton
                                disabled={array.isEmpty(selectedRowsData)}
                                icon={IconName.Delete}
                                onClick={onClick}
                            >
                                <LocalizedText textKey={TextKey.Remove} />
                            </ActionToolbarButton>
                        )}
                    />
                }
            />

            <TableActiveColumnFilters />

            <Table
                data={properties}
                amountOfRows={totalRowCount}
                loading={isFetching}
                rowSelection={{ rowSelection, onRowSelectionChange: setRowSelection }}
                uniqueRowId="id"
                fixedFirstColumn
            />
        </TableProvider>
    );
};
